import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Banner from '../../sections/common/banner';
import DetailsSection from '../../sections/tracking/details.jsx';
import LocationsSection from '../../sections/tracking/locations.jsx';
import TrackingDetailsSection from '../../sections/tracking/trackingDetails.jsx';
import GlassSurface from './GlassSurface.jsx';
import { global } from '../../../globals/constants.js';
import HomePageHelmet from '../../helmets/HomePageHelmet.jsx';

export default function TrackingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refParam = searchParams.get('ref') || '';

  const [value, setValue] = useState(refParam);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [noData, setNoData] = useState(false);
  const resultsRef = useRef(null);
  // Fetch when ref param changes
  useEffect(() => {
    if (!refParam) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `https://admin.mcpl.pk/api/shipments?ref=${encodeURIComponent(
            refParam
          )}`
        );
        const data = await res.json();
        if (data && data?.data?.length > 0) {
          setResults(data?.data[0]?.attributes);
        } else {
          setResults(null);
          setNoData(true); // No data found
        }
      } catch (err) {
        console.error(err);
        setResults({});
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refParam]);

  useEffect(() => {
    if (results && resultsRef.current) {
      const yOffset = -80; // Optional offset for better view
      const y =
        resultsRef.current.getBoundingClientRect().top +
        window.scrollY +
        yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, [results]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    navigate(`?ref=${encodeURIComponent(value)}`);
  };

  const imageUrl = '/assets/images/truck-2.png';

  return (
    <>
      <HomePageHelmet />
      <Banner
        _data={{
          background: global.banner + '1.jpg',
          title: 'Track Shipment',
          crumb: 'Track Shipment',
        }}
      />

      <section
        className='container-fluid d-flex align-items-center py-5 my-5'
        style={{ backgroundColor: 'var(--bs-body-bg)' }}
      >
        <div className='container'>
          <div className='row g-5 align-items-center'>
            <div className='col-12 col-lg-7 position-relative'>
              <div
                className={`z-10 subtle-card p-4 p-sm-5`}
                style={{ background: 'rgba(255, 255, 255, 0.7)' }}
              >
                <form onSubmit={handleSubmit} role='search'>
                  <h3 className='fw-bold mb-3'>Track Your Shipment</h3>
                  <p className='text-body-secondary mb-4'>
                    Enter your tracking ID below to see the latest status of
                    your shipment.
                  </p>
                  <div className='d-flex align-items-center md-align-items-stretch gap-3 lg-gap-2 flex-lg-row flex-column justify-content-center'>
                    <input
                      id='search-input'
                      type='text'
                      className='form-control subtle-input'
                      placeholder='e.g. 01234567890'
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                    />
                    <GlassSurface
                      height={50}
                      width='150px'
                      borderRadius={999}
                      disabled={!value.trim() || loading}
                      onClick={handleSubmit}
                    >
                      {!loading ? (
                        'Track'
                      ) : (
                        <span className='spinner-border spinner-border-sm' />
                      )}
                    </GlassSurface>
                  </div>
                </form>
                <div className='mt-5'>
                  <PlaceholderState />
                </div>
              </div>
            </div>

            <div className='col-12 col-lg-5'>
              <img src={imageUrl} alt='Shipment' className='side-img' />
            </div>
          </div>
        </div>
      </section>

      {/* Show cargo/tracking sections if API returns data */}
      {results && (
        <div ref={resultsRef}>
          <div className='main-cargo'>
            <DetailsSection data={results} />
            <LocationsSection
              className='locations-section'
              data={results?.segments}
              currentLocation={results.currentLocation}
            />
          </div>
          <div className='main-tracking'>
            <h2 className='tracking-header'>Tracking Details</h2>
            <TrackingDetailsSection data={results} />
          </div>
        </div>
      )}
    </>
  );
}
function PlaceholderState() {
  return (
    <div className='text-center text-body-secondary py-5 border rounded-3 bg-body-secondary-subtle'>
      <h5 className='fw-semibold mb-2'>Enter Tracking ID</h5>
      <p className='mb-0'>
        We’ll show you the current status and history of your shipment.
      </p>
    </div>
  );
}
